import {
  AnalogJoint,
  BasicProfile,
  ComponentDesign,
  ComponentSide,
  isProfileTruss,
  Lumber,
  LUMBER_GRADES,
  LUMBER_SPECIES,
  LumberGrade,
  LumberSpecies,
  Member,
} from '@paragon/contract-library';
import { Point2D, Polygon2D } from '@paragon/geometry-library';
import { applyLumberPatch, lumberCanBeUsedForComponentThickness, LumberPropertyLevel } from '@paragon/material-lists';
import { calculateMemberOverallLengthSegment } from '@paragon/three-shared';

import {
  carriedComponentConnectionIsValid,
  getDefaultCarriedComponentConnection,
} from '../../components/interface/subpanels/properties/CarriedComponentConnectionProperties';
import { ProfileClass } from '../../library/profiles/ProfileClass';
import { calculateProfileLineGeometry, calculateWebJointLocation } from '../../library/profiles/profileManipulation';
import type { AppDispatch, RootState } from '../../store';
import { createProfileOfActiveComponentDesign } from '../../store/actions/appActions';
import { deleteItems } from '../../store/actions/deletionActions';
import { adjustComponentSpan, startRewebTruss } from '../../store/actions/editComponentActions';
import {
  adjustCut,
  editLumbers,
  makeWebVertical,
  reverseWeb,
  stackChord,
  stackWeb,
  unstackWeb,
} from '../../store/actions/editMemberActions';
import {
  addComponentSplices,
  addOrUpdateJackGirderConnection,
  cancelInsertion,
  finishInsertGusset,
} from '../../store/actions/insertActions';
import {
  commitInsertComponentDesign,
  regenerateActiveComponentFromSettings,
} from '../../store/actions/insertComponentDesignActions';
import { lockIndividualMember, setLockOptionForComponentDesign } from '../../store/actions/lockOptionsActions';
import {
  shiftWebJointPosition,
  updateInsertingProfile,
  updateInsertingProfileHeelHeight,
  updateProfileJoint,
} from '../../store/actions/profileActions';
import { getActiveComponentSpans } from '../../store/selectors/getActiveComponentSpansSelector';
import { getCarriedComponentDesignCandidatesForActiveComponentDesign } from '../../store/selectors/getJackGirderConnections';
import { getUniqueLumbersInLumberListForActiveComponentDesign } from '../../store/selectors/getUniqueLumbersInLumberList';
import { getInsertingProfileTrussSettingsOrNull } from '../../store/selectors/profileSelectors';
import { applicationOnSetInsertionGussetCenterPoint, clearRewebbingInfo } from '../../store/slices/applicationSlice';
import { setComponent, setComponentPlies, setComponentSpacing } from '../../store/slices/componentDesignsSlice';
import { setTrussEditsPanel } from '../../store/slices/panelDataSlice';
import {
  describeJackConnections,
  describeLumber,
  findJointByName,
  findMemberByName,
  findTrussByName,
  getTrussMembers,
  NO_PROFILE_ALTERNATIVE,
  openTruss,
  optionalNumber,
  requireNumber,
  requireString,
  requireStringArray,
  roundToSixteenth,
  summarizeTruss,
  waitForAppToSettle,
  waitForRewebToStart,
} from './helpers';
import { describeWeakerDesignValues, loadDesignValues } from './lumberStrength';
import { defineTool, ToolError } from './types';

const TRUSS_SCHEMA = { type: 'string', description: 'The truss’s name.' };
export const MEMBER_SCHEMA = {
  type: 'string',
  description:
    'The member’s name as the app shows it, such as T1 or W3, from the latest result for this truss: adding, ' +
    'deleting, splicing, or re-webbing members renumbers the others.',
};

const SIZE_PATTERN = /^\s*(\d+)\s*x\s*(\d+)\s*$/i;

const SPECIES_ABBREVIATIONS: Readonly<Record<string, LumberSpecies>> = {
  syp: 'Southern_Pine',
  sp: 'Southern_Pine',
  spf: 'Spruce_Pine_Fir',
  df: 'Douglas_Fir_Larch',
  dfl: 'Douglas_Fir_Larch',
  hf: 'Hem_Fir',
};

const GRADE_ABBREVIATIONS: Readonly<Record<string, LumberGrade>> = {
  ss: 'Select_Structural',
  dss: 'Dense_Select_Structural',
};

/** Lowercases and drops everything but letters and digits, so "No. 2", "#2", and "Number_2" can be compared. */
function simplify(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Reads a species the way designers write it (SYP, SPF, Southern Pine) as the app’s code for it.
 */
export function parseSpecies(text: string): LumberSpecies {
  const simplified = simplify(text);
  const species =
    SPECIES_ABBREVIATIONS[simplified] ?? LUMBER_SPECIES.find((candidate) => simplify(candidate) === simplified);
  if (species == null) throw new ToolError(`"${text}" isn’t a lumber species the app knows.`);
  return species;
}

/**
 * Reads a grade the way designers write it (#2, No. 1, SS, 2400F-2.0E) as the app’s code for it.
 */
export function parseGrade(text: string): LumberGrade {
  const simplified = simplify(text).replace(/^(no|number)?(\d)$/, 'number$2');
  const grade =
    GRADE_ABBREVIATIONS[simplified] ?? LUMBER_GRADES.find((candidate) => simplify(candidate) === simplified);
  if (grade == null) throw new ToolError(`"${text}" isn’t a lumber grade the app knows.`);
  return grade;
}

/**
 * Builds the lumber a member should change to, snapped to the closest lumber in the truss’s lumber list, as the
 * properties panel does.
 */
export function chooseLumber(
  member: Member,
  request: { readonly size?: string; readonly species?: string; readonly grade?: string },
  lumberList: readonly Lumber[],
): Lumber {
  const patch: { -readonly [Key in keyof Lumber]?: Lumber[Key] } = {};
  if (request.size != null) {
    const match = request.size.match(SIZE_PATTERN);
    if (match == null) throw new ToolError(`"${request.size}" isn’t a lumber size like 2x6.`);
    patch.nominalThickness = match[1];
    patch.nominalWidth = match[2];
  }
  if (request.species != null) patch.species = parseSpecies(request.species);
  if (request.grade != null) patch.grade = parseGrade(request.grade);

  const validLumbers = lumberList.filter((lumber) => lumberCanBeUsedForComponentThickness(lumber, member.thickness));
  if (validLumbers.length === 0) throw new ToolError(`No lumber in the lumber list fits ${member.name}.`);

  // A truss's members are all as thick as the truss, so a size that doesn't fit it can't be had in any grade.
  if (
    patch.nominalThickness != null &&
    !validLumbers.some(
      (lumber) => lumber.nominalThickness === patch.nominalThickness && lumber.nominalWidth === patch.nominalWidth,
    )
  )
    throw new ToolError(
      `${member.name} can’t be ${request.size}: the truss is ${member.thickness} in thick, and its members must ` +
        `match. Sizes that fit: ${[...new Set(validLumbers.map((l) => `${l.nominalThickness}x${l.nominalWidth}`))].join(', ')}.`,
    );

  const lumber = applyLumberPatch(member.lumber, { patch, level: LumberPropertyLevel.Treatment }, validLumbers);
  const mismatches = (Object.keys(patch) as (keyof Lumber)[]).filter((key) => lumber[key] !== patch[key]);
  if (mismatches.length > 0)
    throw new ToolError(
      `The lumber list has no ${[request.size, request.species, request.grade].filter(Boolean).join(' ')} for ` +
        `${member.name}. The closest is ${lumber.nominalThickness}x${lumber.nominalWidth} ${lumber.species} ` +
        `${lumber.grade}.`,
    );
  return lumber;
}

/**
 * Runs an edit that goes through the app’s Regenerate from Settings flow, and commits it. If the edit leaves the
 * regeneration open, it's cancelled so the truss isn't left mid-edit.
 */
export async function regenerateFromSettings(
  dispatch: AppDispatch,
  getState: () => RootState,
  edit: () => Promise<void>,
) {
  await dispatch(regenerateActiveComponentFromSettings());
  await waitForAppToSettle(getState);
  try {
    await edit();
    await waitForAppToSettle(getState);
    await dispatch(commitInsertComponentDesign());
    await waitForAppToSettle(getState);
  } finally {
    if (getState().application.insertingObjectInfo?.type === 'componentDesign') dispatch(cancelInsertion());
  }
}

/**
 * The top chord lines of a profile, with which side of the peak each is on.
 */
function getTopChordLines(state: RootState) {
  const settings = getInsertingProfileTrussSettingsOrNull(state);
  if (settings == null) throw new ToolError('Could not open this truss’s settings.');
  const { profile } = settings;

  return profile.profileLines
    .filter((line) => line.profileLineType === 'TopChord')
    .map((line) => {
      const segment = calculateProfileLineGeometry(profile, line);
      const rising = segment.basePoint.y < segment.endPoint.y;
      const lower = rising ? segment.basePoint : segment.endPoint;
      const higher = rising ? segment.endPoint : segment.basePoint;
      const higherJointGuid = rising ? line.endJointGuid : line.baseJointGuid;
      return {
        line,
        lower,
        higher,
        higherJoint: profile.profileJoints.find((joint) => joint.guid === higherJointGuid)!,
        side: (lower.x < higher.x ? 'Left' : 'Right') as ComponentSide,
      };
    })
    .filter(({ lower, higher }) => Math.abs(higher.y - lower.y) > 1e-6 && Math.abs(higher.x - lower.x) > 1e-6);
}

const WEB_JOINT_TOLERANCE_IN_INCHES = 6;

/**
 * Finds the profile web joint that an analysis joint sits on, if any.
 */
export function findNearestWebJoint(profile: BasicProfile, joint: Pick<AnalogJoint, 'x' | 'y'>) {
  let nearest: { guid: string; distance: number } | null = null;

  for (const profileLine of profile.profileLines) {
    const segment = calculateProfileLineGeometry(profile, profileLine);
    const webJoints = profileLine.fixedJoints.concat(
      profileLine.webJointSegments.flatMap((webJointSegment) => webJointSegment.floatingJoints),
    );

    for (const webJoint of webJoints) {
      const location = calculateWebJointLocation(segment, webJoint);
      const distance = Math.hypot(location.x - joint.x, location.y - joint.y);
      if (nearest == null || distance < nearest.distance) nearest = { guid: webJoint.guid, distance };
    }
  }

  return nearest != null && nearest.distance <= WEB_JOINT_TOLERANCE_IN_INCHES ? nearest : null;
}

/**
 * Finds the profile joint, such as the peak or a heel, that an analysis joint sits on, if any.
 */
export function findNearestProfileJoint(profile: BasicProfile | null, joint: Pick<AnalogJoint, 'x' | 'y'>) {
  if (profile == null) return null;
  let nearest: { guid: string; distance: number } | null = null;
  for (const profileJoint of profile.profileJoints) {
    const distance = Math.hypot(profileJoint.location.x - joint.x, profileJoint.location.y - joint.y);
    if (nearest == null || distance < nearest.distance) nearest = { guid: profileJoint.guid, distance };
  }
  return nearest != null && nearest.distance <= WEB_JOINT_TOLERANCE_IN_INCHES ? nearest : null;
}

async function finishTrussEdit(getState: () => RootState, componentDesign: ComponentDesign) {
  await waitForAppToSettle(getState);
  const edited = getState().componentDesigns.data[componentDesign.guid];
  if (edited == null) throw new ToolError('The truss is no longer in the project.');
  return { summary: summarizeTruss(edited) };
}

export const trussTools = [
  defineTool<{ truss: string; members: string[]; size?: string; species?: string; grade?: string }>({
    name: 'set_lumber',
    description:
      'Changes members’ lumber, as the properties panel does. Give any of size (2x6), species, and grade; the rest ' +
      'stay. Members whose species or grade change are locked so Upgrade and Analyze keeps them.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        members: { type: 'array', items: MEMBER_SCHEMA },
        size: { type: 'string', description: 'Nominal size, such as 2x6.' },
        species: { type: 'string', description: 'For example SYP, SPF, or Southern_Pine.' },
        grade: { type: 'string', description: 'For example #2, No. 1, SS, or Number_2.' },
      },
      required: ['truss', 'members'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      if (input.size == null && input.species == null && input.grade == null)
        throw new ToolError('Give a size, species, or grade.');

      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const lumberList = getUniqueLumbersInLumberListForActiveComponentDesign(getState());
      const updates = requireStringArray(input, 'members').map((name): [Member, Lumber] => {
        const member = findMemberByName(componentDesign, name);
        return [member, chooseLumber(member, input, lumberList)];
      });

      dispatch(editLumbers(componentDesign.guid, updates));
      const result = await finishTrussEdit(getState, componentDesign);
      // Say plainly when a change makes a member weaker, so a grade picked by name alone doesn't go unnoticed.
      const designValues = await loadDesignValues(
        dispatch,
        getState,
        updates.flatMap(([member, lumber]) => [member.lumber, lumber]),
      );
      return {
        summary: {
          changed: updates.map(([member, lumber]) => {
            const before = designValues(member.lumber);
            const after = designValues(lumber);
            const weaker = before == null || after == null ? [] : describeWeakerDesignValues(before, after);
            return {
              member: member.name,
              lumber: describeLumber(
                findMemberByName(getState().componentDesigns.data[componentDesign.guid], member.name),
              ),
              ...(weaker.length > 0 ? { weaker } : {}),
            };
          }),
          truss: result.summary,
        },
      };
    },
  }),

  defineTool<{ truss: string; member: string; x?: number; distanceFromLeft?: number }>({
    name: 'splice_chord',
    description:
      'Splices a chord where a drawing has a splice. Give `x`, inches from the truss’s left end as the drawing ' +
      'dimensions it, or `distanceFromLeft`, inches along the member from its own left end (along the slope for a ' +
      'sloped chord). Splicing renames members: splitting T1 makes T1 and T2, and the old T2 becomes T3. Returns ' +
      'the new member names. To move a splice, delete it with delete_splices, then splice the joined chord where it ' +
      'belongs.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        member: MEMBER_SCHEMA,
        x: { type: 'number', description: 'Inches from the truss’s left end.' },
        distanceFromLeft: { type: 'number', description: 'Inches along the member from its left end.' },
      },
      required: ['truss', 'member'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const member = findMemberByName(componentDesign, requireString(input, 'member'));
      if (member.memberType !== 'TopChord' && member.memberType !== 'BottomChord')
        throw new ToolError(`${member.name} isn’t a chord.`);

      const x = optionalNumber(input, 'x');
      let distance = optionalNumber(input, 'distanceFromLeft');
      if ((x == null) === (distance == null)) throw new ToolError('Give either "x" or "distanceFromLeft".');
      if (x != null) {
        const centerline = calculateMemberOverallLengthSegment(member, componentDesign.component);
        if (centerline == null) throw new ToolError(`The app couldn’t find where ${member.name} runs.`);
        const [left, right] =
          centerline.start.x <= centerline.end.x
            ? [centerline.start, centerline.end]
            : [centerline.end, centerline.start];
        if (x <= left.x || x >= right.x)
          throw new ToolError(
            `${member.name} runs from x = ${roundToSixteenth(left.x)} to ${roundToSixteenth(right.x)}.`,
          );
        distance = ((x - left.x) / (right.x - left.x)) * Math.hypot(right.x - left.x, right.y - left.y);
      }

      const splicesBefore = componentDesign.component.chordSplices.length;
      await dispatch(addComponentSplices(componentDesign, member, distance!));
      const result = await finishTrussEdit(getState, componentDesign);
      if (getState().componentDesigns.data[componentDesign.guid].component.chordSplices.length === splicesBefore)
        throw new ToolError(`The app could not splice ${member.name} there.`);
      return result;
    },
  }),

  defineTool<{ truss: string; splices: string[] }>({
    name: 'delete_splices',
    description:
      'Deletes chord splices, as selecting a splice and pressing Delete does, joining the two chord members it ' +
      'separates into one. Name each splice as the truss lists it, where its position is given too. Run Upgrade ' +
      'and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, splices: { type: 'array', items: { type: 'string' } } },
      required: ['truss', 'splices'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const names = requireStringArray(input, 'splices');
      if (names.length === 0) throw new ToolError('Name at least one splice.');
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const splices = names.map((name) => {
        const splice = componentDesign.component.chordSplices.find(
          (candidate) => candidate.name.toLowerCase() === name.trim().toLowerCase(),
        );
        if (splice == null)
          throw new ToolError(
            `There is no splice "${name}". The splices are ` +
              `${componentDesign.component.chordSplices.map((candidate) => candidate.name).join(', ') || 'none'}.`,
          );
        return splice;
      });

      // The app deletes one splice at a time.
      for (const splice of splices) {
        await dispatch(deleteItems([{ type: 'splice', item: splice }]));
        await waitForAppToSettle(getState);
      }

      const edited = getState().componentDesigns.data[componentDesign.guid];
      const remaining = splices.filter((splice) =>
        edited.component.chordSplices.some((candidate) => candidate.guid === splice.guid),
      );
      if (remaining.length > 0)
        throw new ToolError(
          `The app didn’t delete ${remaining.map((splice) => splice.name).join(', ')}. Check its notifications for why.`,
        );
      return { summary: summarizeTruss(edited) };
    },
  }),

  defineTool<{
    truss: string;
    bearingSpan?: number;
    overallLength?: number;
    leftOverhang?: number;
    rightOverhang?: number;
    pitch?: number;
    pitchSide?: 'Left' | 'Right' | 'Both';
    heelHeight?: number;
    heelSide?: 'Left' | 'Right' | 'Both';
  }>({
    name: 'reshape_truss',
    description:
      'Reshapes a truss from its settings: bearing span or overall length, overhangs, top chord pitch (rise per 12), ' +
      'and heel height. Lengths are in inches. Plates are dropped, so run Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        bearingSpan: { type: 'number' },
        overallLength: { type: 'number' },
        leftOverhang: { type: 'number' },
        rightOverhang: { type: 'number' },
        pitch: { type: 'number', description: 'Rise per 12 for the top chords.' },
        pitchSide: { type: 'string', enum: ['Left', 'Right', 'Both'], description: 'Defaults to Both.' },
        heelHeight: { type: 'number' },
        heelSide: { type: 'string', enum: ['Left', 'Right', 'Both'], description: 'Defaults to Both.' },
      },
      required: ['truss'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const bearingSpan = optionalNumber(input, 'bearingSpan');
      const overallLength = optionalNumber(input, 'overallLength');
      const leftOverhang = optionalNumber(input, 'leftOverhang');
      const rightOverhang = optionalNumber(input, 'rightOverhang');
      const pitch = optionalNumber(input, 'pitch');
      const heelHeight = optionalNumber(input, 'heelHeight');

      if ([bearingSpan, overallLength, leftOverhang, rightOverhang, pitch, heelHeight].every((v) => v == null))
        throw new ToolError('Give at least one of bearingSpan, overallLength, overhangs, pitch, or heelHeight.');
      if (bearingSpan != null && overallLength != null)
        throw new ToolError('Give bearingSpan or overallLength, not both.');

      const isProfile = isProfileTruss(componentDesign.component);
      if (!isProfile && (leftOverhang != null || rightOverhang != null || pitch != null || heelHeight != null))
        throw new ToolError('Overhangs, pitch, and heel height can only be changed on profile trusses.');

      // Pitch, heels, and overhangs all change in one regeneration from settings, which rebuilds the truss from its
      // stored profile. The span changes afterward, since the app only adjusts span on the built truss, and the
      // stored profile is then rebuilt from the result so a later regeneration doesn't undo it.
      if (isProfile && (pitch != null || heelHeight != null || leftOverhang != null || rightOverhang != null))
        await regenerateFromSettings(dispatch, getState, async () => {
          if (heelHeight != null) {
            const sides: ComponentSide[] =
              input.heelSide == null || input.heelSide === 'Both' ? ['Left', 'Right'] : [input.heelSide];
            for (const side of sides) {
              const settings = getInsertingProfileTrussSettingsOrNull(getState());
              if (settings == null) throw new ToolError('Could not open this truss’s settings.');
              const profileClass = ProfileClass.create(settings);
              if (profileClass.type === 'Err')
                throw new ToolError(`The truss’s profile is invalid: ${profileClass.err}`);
              await dispatch(updateInsertingProfileHeelHeight(profileClass.ok, heelHeight, side));
            }
          }

          if (pitch != null) {
            const sides = input.pitchSide == null || input.pitchSide === 'Both' ? ['Left', 'Right'] : [input.pitchSide];
            const lines = getTopChordLines(getState()).filter((line) => sides.includes(line.side));
            if (lines.length === 0) throw new ToolError('This truss has no sloped top chord to change.');
            for (const { lower, higher, higherJoint } of lines) {
              const run = Math.abs(higher.x - lower.x);
              await dispatch(updateProfileJoint(higherJoint, higherJoint.location.x, lower.y + (run * pitch) / 12));
            }
          }

          if (leftOverhang != null || rightOverhang != null) {
            const profile = getInsertingProfileTrussSettingsOrNull(getState())?.profile;
            if (profile == null) throw new ToolError('Could not open this truss’s settings.');
            await dispatch(
              updateInsertingProfile({
                ...profile,
                leftOverhang: { ...profile.leftOverhang, distance: leftOverhang ?? profile.leftOverhang.distance },
                rightOverhang: { ...profile.rightOverhang, distance: rightOverhang ?? profile.rightOverhang.distance },
              }),
            );
          }
        });

      if (bearingSpan != null || overallLength != null) {
        const spans = getActiveComponentSpans(getState());
        if (spans == null) throw new ToolError('The app can’t adjust this truss’s span.');
        const adjustment = bearingSpan != null ? bearingSpan - spans.bearingSpan : overallLength! - spans.overallLength;
        if (Math.abs(adjustment) > 1 / 16) {
          await dispatch(adjustComponentSpan(adjustment, componentDesign.guid));
          await waitForAppToSettle(getState);
          if (isProfile) {
            await dispatch(createProfileOfActiveComponentDesign());
            await waitForAppToSettle(getState);
          }
        }
      }

      return finishTrussEdit(getState, componentDesign);
    },
  }),

  defineTool<{ truss: string; plies?: number; spacing?: number }>({
    name: 'set_plies_and_spacing',
    description: 'Changes a truss’s number of plies or its spacing in inches.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, plies: { type: 'number' }, spacing: { type: 'number' } },
      required: ['truss'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const component = componentDesign.component;
      if (!('numberOfPlies' in component)) throw new ToolError('Only trusses have plies and spacing.');

      const plies = optionalNumber(input, 'plies');
      const spacing = optionalNumber(input, 'spacing');
      if (plies == null && spacing == null) throw new ToolError('Give plies or spacing.');
      if (plies != null && (!Number.isInteger(plies) || plies < 1)) throw new ToolError('Plies must be 1 or more.');

      const minimumSpacing = (plies ?? component.numberOfPlies) * component.thickness;
      if (spacing != null && spacing < minimumSpacing)
        throw new ToolError(`Spacing must be at least ${minimumSpacing} inches for this many plies.`);

      if (plies != null) dispatch(setComponentPlies({ componentDesignGuid: componentDesign.guid, plies }));
      if (spacing != null) dispatch(setComponentSpacing({ componentDesignGuid: componentDesign.guid, spacing }));
      return finishTrussEdit(getState, componentDesign);
    },
  }),

  defineTool<{ truss: string; members?: string[]; allMembers?: boolean; plating?: boolean; lock: boolean }>({
    name: 'lock',
    description:
      'Locks or unlocks members so Upgrade and Analyze keeps their lumber, or locks a truss’s plating. Give members ' +
      'by name, or allMembers to lock every member.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        members: { type: 'array', items: MEMBER_SCHEMA },
        allMembers: { type: 'boolean' },
        plating: { type: 'boolean' },
        lock: { type: 'boolean', description: 'True to lock, false to unlock.' },
      },
      required: ['truss', 'lock'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (typeof input.lock !== 'boolean') throw new ToolError('"lock" must be true or false.');
      if (input.members == null && !input.allMembers && input.plating == null)
        throw new ToolError('Give members, allMembers, or plating.');

      if (input.allMembers)
        dispatch(setLockOptionForComponentDesign(componentDesign.guid, 'membersLocked', input.lock));
      if (input.plating) dispatch(setLockOptionForComponentDesign(componentDesign.guid, 'platingLocked', input.lock));
      if (input.members != null) {
        if (getState().componentDesigns.data[componentDesign.guid].membersLocked && !input.lock)
          throw new ToolError('Every member is locked. Unlock them all with allMembers first.');
        const members = requireStringArray(input, 'members').map((name) => findMemberByName(componentDesign, name));
        dispatch(lockIndividualMember(input.lock, ...members.map((member) => member.guid)));
      }
      return finishTrussEdit(getState, componentDesign);
    },
  }),

  defineTool<{
    girder: string;
    jack: string;
    at: number;
    jackBearing?: string;
    numberOfJacks?: number;
    spacingOfJacks?: number;
    chord?: 'BottomChord' | 'TopChord';
  }>({
    name: 'connect_jacks_to_girder',
    description:
      'Connects jacks to a girder so the girder carries their load. `at` is where the first jack lands, in inches ' +
      'from the left end of the girder. Can’t be undone in the app, so check get_truss’s jackConnections first: a ' +
      'jack already connected shouldn’t be connected again. Returns the girder’s connections. Run Upgrade and ' +
      'Analyze on the girder afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        girder: { type: 'string', description: 'The girder truss’s name.' },
        jack: { type: 'string', description: 'The jack truss’s name.' },
        at: { type: 'number' },
        jackBearing: { type: 'string', description: 'The jack’s bearing on the girder, if it has more than one.' },
        numberOfJacks: { type: 'number', description: 'Defaults to 1.' },
        spacingOfJacks: { type: 'number', description: 'Inches. Defaults to 24.' },
        chord: { type: 'string', enum: ['BottomChord', 'TopChord'], description: 'Defaults to BottomChord.' },
      },
      required: ['girder', 'jack', 'at'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const girder = await openTruss(dispatch, getState, requireString(input, 'girder'));
      const jack = findTrussByName(getState(), requireString(input, 'jack'));
      if (
        !getCarriedComponentDesignCandidatesForActiveComponentDesign(getState()).some(({ guid }) => guid === jack.guid)
      )
        throw new ToolError(`${jack.component.name} can’t be carried by ${girder.component.name}.`);

      const bearings = jack.component.bearings;
      const bearing =
        input.jackBearing != null
          ? bearings.find((candidate) => candidate.name.toLowerCase() === input.jackBearing!.toLowerCase())
          : bearings.length === 1
            ? bearings[0]
            : null;
      if (bearing == null)
        throw new ToolError(
          `Say which of ${jack.component.name}’s bearings sits on the girder: ` +
            `${bearings.map((candidate) => candidate.name).join(', ')}.`,
        );

      const connection = {
        ...getDefaultCarriedComponentConnection(girder.guid),
        jackGuid: jack.guid,
        jackBearingGuid: bearing.guid,
        xLocation: requireNumber(input, 'at'),
        memberType: input.chord ?? 'BottomChord',
        ...(input.numberOfJacks != null ? { numberOfJacks: input.numberOfJacks } : {}),
        ...(input.spacingOfJacks != null ? { spacingOfJacks: input.spacingOfJacks } : {}),
      };
      if (
        !carriedComponentConnectionIsValid(
          connection,
          girder,
          getCarriedComponentDesignCandidatesForActiveComponentDesign(getState()),
        )
      )
        throw new ToolError(`A jack can’t land at ${connection.xLocation} inches on that chord of the girder.`);

      const connectionsBefore = Object.keys(getState().jackGirderConnections.data).length;
      await dispatch(addOrUpdateJackGirderConnection(connection, 'add'));
      await waitForAppToSettle(getState);
      if (Object.keys(getState().jackGirderConnections.data).length === connectionsBefore)
        throw new ToolError('The app could not save the connection.');

      // Read back what the app saved, with the girder's other connections, so a retry can see what's already hung.
      return {
        summary: { girder: girder.component.name, connections: describeJackConnections(getState(), girder.guid) },
      };
    },
  }),

  defineTool<{
    truss: string;
    member: string;
    edit: 'stack' | 'unstack' | 'reverse' | 'makeVertical' | 'recutClockwise' | 'recutCounterclockwise';
    end?: 'lower' | 'upper';
  }>({
    name: 'edit_web',
    description:
      'Edits a web as the member menu does: stack or unstack it, reverse it, make it vertical, or recut one end ' +
      'clockwise or counterclockwise. `end` chooses which end to move for makeVertical and recuts.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        member: MEMBER_SCHEMA,
        edit: {
          type: 'string',
          enum: ['stack', 'unstack', 'reverse', 'makeVertical', 'recutClockwise', 'recutCounterclockwise'],
        },
        end: { type: 'string', enum: ['lower', 'upper'], description: 'Defaults to lower.' },
      },
      required: ['truss', 'member', 'edit'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const member = findMemberByName(componentDesign, requireString(input, 'member'));
      const adjustLowerEnd = (input.end ?? 'lower') === 'lower';

      switch (input.edit) {
        case 'stack':
          await dispatch(stackWeb(member.guid));
          break;
        case 'unstack':
          await dispatch(unstackWeb(member.guid));
          break;
        case 'reverse':
          await dispatch(reverseWeb(member.guid));
          break;
        case 'makeVertical':
          await dispatch(makeWebVertical(member.guid, adjustLowerEnd));
          break;
        case 'recutClockwise':
          await dispatch(adjustCut(member.guid, adjustLowerEnd, true));
          break;
        case 'recutCounterclockwise':
          await dispatch(adjustCut(member.guid, adjustLowerEnd, false));
          break;
        default:
          throw new ToolError(`Unknown edit "${input.edit}".`);
      }
      const result = await finishTrussEdit(getState, componentDesign);
      // The app leaves the truss as it was when an edit doesn't apply, such as unstacking a web that isn't stacked.
      if (JSON.stringify(result.summary) === JSON.stringify(summarizeTruss(componentDesign)))
        throw new ToolError(`The app made no change: ${input.edit} doesn’t apply to ${member.name} as it is.`);
      return result;
    },
  }),

  defineTool<{ truss: string; members: string[] }>({
    name: 'delete_members',
    description:
      'Deletes members from a truss, as selecting them and pressing Delete does, for example a web the drawing ' +
      'doesn’t have. Re-webbing or regenerating the truss later can bring webs back, so delete after those. Keep ' +
      'the end verticals and chords unless you redraw them: members can only be drawn inside a closed truss. Run ' +
      'Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, members: { type: 'array', items: MEMBER_SCHEMA } },
      required: ['truss', 'members'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const names = requireStringArray(input, 'members');
      if (names.length === 0) throw new ToolError('Name at least one member.');
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const members = names.map((name) => findMemberByName(componentDesign, name));

      await dispatch(deleteItems(members.map((member) => ({ type: 'member' as const, item: member }))));
      await waitForAppToSettle(getState);

      const edited = getState().componentDesigns.data[componentDesign.guid];
      if (edited == null) throw new ToolError('The truss is no longer in the project.');
      // Braces count too: a brace the app kept is still there.
      const remaining = members.filter((member) =>
        getTrussMembers(edited).some((candidate) => candidate.guid === member.guid),
      );
      if (remaining.length > 0)
        throw new ToolError(
          `The app didn’t delete ${remaining.map((member) => member.name).join(', ')}. Check its notifications for why.`,
        );
      return { summary: summarizeTruss(edited) };
    },
  }),

  defineTool<{ truss: string; joint: string; dx?: number; dy?: number }>({
    name: 'move_joint',
    description:
      'Moves a joint by dx inches (right is positive) and dy inches (up is positive). Name it as the analysis does ' +
      '(Joint 11). A web joint on a chord slides along the chord by dx, as dragging it in Re-web does, and the truss ' +
      're-webs. A profile joint, such as the peak or a heel, moves by dx and dy through the truss’s settings, as ' +
      'Regenerate from Settings does. Profile trusses only: get_truss’s definition must be Profile; floor and other ' +
      'pattern trusses have no profile to move.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, joint: { type: 'string' }, dx: { type: 'number' }, dy: { type: 'number' } },
      required: ['truss', 'joint'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!isProfileTruss(componentDesign.component))
        throw new ToolError(`Only profile trusses’ joints can be moved. ${NO_PROFILE_ALTERNATIVE}`);
      const joint = findJointByName(componentDesign, requireString(input, 'joint'));
      const dx = optionalNumber(input, 'dx') ?? 0;
      const dy = optionalNumber(input, 'dy') ?? 0;
      if (dx === 0 && dy === 0) throw new ToolError('Give dx or dy.');

      const profileJoint = findNearestProfileJoint(componentDesign.profile, joint);
      if (profileJoint != null) {
        await regenerateFromSettings(dispatch, getState, async () => {
          const settings = getInsertingProfileTrussSettingsOrNull(getState());
          const current = settings?.profile.profileJoints.find((candidate) => candidate.guid === profileJoint.guid);
          if (current == null) throw new ToolError('Could not open this truss’s settings.');
          await dispatch(updateProfileJoint(current, current.location.x + dx, current.location.y + dy));
        });
        return finishTrussEdit(getState, componentDesign);
      }

      if (dy !== 0) throw new ToolError(`Joint ${joint.name} is a web joint; it only slides along its chord (dx).`);
      // Re-web would rebuild an attic without its room.
      if (componentDesign.profile?.atticSettings != null)
        throw new ToolError(
          `Joint ${joint.name} is an attic web joint, which Re-web can’t move without losing the room. Delete the ` +
            'webs there with delete_members and draw them where they belong with add_members.',
        );

      // Starting Re-web re-webs the truss and leaving it keeps that, so a move that fails puts the truss back.
      const before = componentDesign.component;
      await dispatch(startRewebTruss());
      await waitForRewebToStart(getState);
      try {
        const profile = getState().application.rewebbingInfo?.profileSettings.profile;
        if (profile == null) throw new ToolError('Could not start re-webbing this truss.');

        const webJoint = findNearestWebJoint(profile, joint);
        if (webJoint == null)
          throw new ToolError(`Joint ${joint.name} isn’t on the truss’s profile or a chord, so it can’t be moved.`);

        await dispatch(shiftWebJointPosition(webJoint.guid, dx));
        await waitForAppToSettle(getState);
      } catch (error) {
        dispatch(clearRewebbingInfo());
        if (getState().componentDesigns.data[componentDesign.guid]?.component !== before) {
          dispatch(setComponent({ componentDesignGuid: componentDesign.guid, component: before }));
          await waitForAppToSettle(getState);
        }
        throw error;
      } finally {
        dispatch(clearRewebbingInfo());
        dispatch(setTrussEditsPanel(null));
      }
      return finishTrussEdit(getState, componentDesign);
    },
  }),

  defineTool<{ truss: string; at: { x: number; y: number }; height?: number; width?: number; material?: string }>({
    name: 'add_gusset',
    description:
      'Adds a gusset centered on a point in the truss’s coordinates (inches from the left end of the bottom chord, up ' +
      'positive). The app aligns it with the nearest edge of the truss, so check where it landed in the result’s ' +
      'gussets. Defaults to 24 by 48 inches of OSB.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        at: { type: 'object', properties: { x: { type: 'number' }, y: { type: 'number' } }, required: ['x', 'y'] },
        height: { type: 'number' },
        width: { type: 'number' },
        material: { type: 'string' },
      },
      required: ['truss', 'at'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const at = input.at as { x?: unknown; y?: unknown } | undefined;
      if (typeof at?.x !== 'number' || typeof at?.y !== 'number') throw new ToolError('"at" must have x and y.');

      const gussetsBefore = componentDesign.component.gussets.length;
      const material =
        input.material ??
        (isProfileTruss(componentDesign.component)
          ? '7/16" 24/16 span rated OSB'
          : '23/32" 48/24 or 24 OC span rated OSB');

      // The same state the canvas click leaves behind, so the app’s own Insert finishes it.
      dispatch(applicationOnSetInsertionGussetCenterPoint({ x: at.x, y: at.y }));
      await dispatch(finishInsertGusset(input.height ?? 2 * 12, input.width ?? 4 * 12, material));
      if (getState().application.insertingObjectInfo?.type === 'gusset') dispatch(cancelInsertion());

      const result = await finishTrussEdit(getState, componentDesign);
      if (getState().componentDesigns.data[componentDesign.guid].component.gussets.length === gussetsBefore)
        throw new ToolError('The app could not add a gusset there.');
      return result;
    },
  }),

  defineTool<{ truss: string; at: { x: number; y: number } }>({
    name: 'delete_gusset',
    description:
      'Deletes the gusset that covers a point in the truss’s coordinates, as selecting it and pressing Delete does. ' +
      'Gussets have no names; get_truss lists each one’s outline.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        at: { type: 'object', properties: { x: { type: 'number' }, y: { type: 'number' } }, required: ['x', 'y'] },
      },
      required: ['truss', 'at'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const at = input.at as { x?: unknown; y?: unknown } | undefined;
      if (typeof at?.x !== 'number' || typeof at?.y !== 'number') throw new ToolError('"at" must have x and y.');
      const point = new Point2D(at.x, at.y);
      const gusset = componentDesign.component.gussets.find((candidate) => {
        try {
          return new Polygon2D(candidate.geometry.map((corner) => Point2D.fromObject(corner))).containsPoint(point);
        } catch {
          return false;
        }
      });
      if (gusset == null) throw new ToolError(`No gusset covers (${at.x}, ${at.y}). get_truss lists their outlines.`);

      await dispatch(deleteItems([{ type: 'gusset', item: gusset }]));
      await waitForAppToSettle(getState);
      const edited = getState().componentDesigns.data[componentDesign.guid];
      if (edited == null) throw new ToolError('The truss is no longer in the project.');
      if (edited.component.gussets.some((candidate) => candidate.guid === gusset.guid))
        throw new ToolError('The app didn’t delete the gusset. Check its notifications for why.');
      return { summary: summarizeTruss(edited) };
    },
  }),

  defineTool<{ truss: string; member: string; from: number; to: number }>({
    name: 'stack_chord',
    description: 'Stacks a chord between two x positions in inches in the truss’s coordinates.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, member: MEMBER_SCHEMA, from: { type: 'number' }, to: { type: 'number' } },
      required: ['truss', 'member', 'from', 'to'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const member = findMemberByName(componentDesign, requireString(input, 'member'));
      await dispatch(stackChord(componentDesign, member, requireNumber(input, 'from'), requireNumber(input, 'to')));
      return finishTrussEdit(getState, componentDesign);
    },
  }),
];
